import type {
  IPresignedUrlResponseModel,
  IPresignedUrlViewModel,
  IExtractedResultModel,
  IExtractedResultViewModel,
  IExtractedItemViewModel,
  IExtractedItemResponse,
  IExtractedNodeReponse,
  IExtractedNodeViewModel,
  IProcessItemModel,
  IProcessItemViewModel,
  IProcessInfoModel,
  IProcessInfoViewModel,
  IDisplayImageViewModel,
  IDisplayImageModel,
  INotificationMessage,
  INotificationMessageViewModel,
  IProcessItemDetailModel,
  IProcessItemDetailViewModel,
  IProcessDetailResponse,
  IProcessDetailViewModel,
} from "@models/extraction";

export const buildDisplayImageViewModel = (
  displayImage: IDisplayImageModel,
): IDisplayImageViewModel => ({
  pageNumber: displayImage.page_number,
  url: displayImage.url,
});

export const buildPresignedUrlViewModel = (
  response: IPresignedUrlResponseModel,
): IPresignedUrlViewModel => ({
  id: response.id,
  uploadUrl: response.upload_url,
  expiresIn: response.expires_in,
});

export const buildExtractedItemViewModel = (
  field: IExtractedItemResponse,
): IExtractedItemViewModel => ({
  id: field.id,
  key: field.key,
  value: field.value,
  page: field.page,
  uom: field.uom,
  bbox: field.bbox,
  conf: field.conf,
});

export const buildExtractedNodeViewModel = (
  response: IExtractedNodeReponse,
): IExtractedNodeViewModel => ({
  id: response.id,
  name: response.name,
  displayName: response.display_name,
  level: response.level,
  isExpandable: response.is_expandable,
  children: response.children.map(buildExtractedNodeViewModel),
  data: response.data ? buildExtractedItemViewModel(response.data) : null,
});

export const buildExtractedResultViewModel = (
  response: IExtractedResultModel,
): IExtractedResultViewModel => {
  const isPdf = response.is_pdf ?? false;
  const noPages = response.no_pages ?? 1;

  return {
    displayNode: response.display_nodes.map(buildExtractedNodeViewModel),
    errorMessage: response.error_message,
    previewUrl: response.preview_url,
    isPdf,
    noPages,
  };
};

export const buildProcessItemViewModel = (
  item: IProcessItemModel,
): IProcessItemViewModel => ({
  id: item.id,
  fileName: item.file_name,
  status: item.status,
  demoImage: item.demo_image,
  createdAt: item.created_at,
  errorMessage: item.error_message,
});

export const buildProcessedInfoViewModel = (
  response: IProcessInfoModel,
): IProcessInfoViewModel => ({
  id: response.id,
  status: response.status,
  createdAt: response.created_at,
  isBatch: response.is_batch,
  errorMessage: response.error_message,
  items: response.items.map(buildProcessItemViewModel),
});

export const buildListProcessedInfoViewModel = (
  responses: IProcessInfoModel[],
): IProcessInfoViewModel[] => {
  return responses.map(buildProcessedInfoViewModel);
};

export const buildNotificationMessageViewModel = (
  message: INotificationMessage,
): INotificationMessageViewModel => ({
  type: message.type,
  message: message.message,
  status: message.status,
  batchItemId: message.batch_item_id,
  batchItemStatus: message.batch_item_status,
  processId: message.process_id,
  timestamp: message.timestamp,
});

export const buildProcessItemDetailViewModel = (
  item: IProcessItemDetailModel,
): IProcessItemDetailViewModel => ({
  id: item.id,
  idShort: item.id.substring(0, 8),
  fileName: item.file_name,
  status: item.status,
  errorMessage: item.error_message,
  createdAt: item.created_at,
});

/**
 * Normalise `user_input.page_numbers` into a `number[]`.
 *
 * The backend has returned this field as several shapes over time — a
 * comma-separated string ("1,2,3"), a real array ([1, 2, 3] or ["1", "2"]),
 * a single number, or null/undefined. The previous `(x ?? "").split(",")`
 * assumed a string and threw `.split is not a function` on the array shape.
 */
const normalizePageNumbers = (raw: unknown): number[] => {
  const toInt = (v: unknown): number => parseInt(String(v).trim(), 10);
  const values: unknown[] = Array.isArray(raw)
    ? raw
    : raw === null || raw === undefined || raw === ""
      ? []
      : String(raw).split(",");
  return values.map(toInt).filter((n) => !Number.isNaN(n));
};

export const buildProcessDetailViewModel = (
  response: IProcessDetailResponse,
): IProcessDetailViewModel => ({
  id: response.id,
  status: response.status,
  progress: {
    total: response.progress.total,
    completed: response.progress.completed,
  },
  isBatch: response.is_batch,
  startAt: response.start_at,
  finishedAt: response.finished_at,
  errorMessage: response.error_message,
  userInput: {
    pageNumbers: normalizePageNumbers(response.user_input.page_numbers),
    instructions: response.user_input.instructions ?? "",
  },
  processItems: response.process_items.map(buildProcessItemDetailViewModel),
});
